import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import TranslateRoundedIcon from "@mui/icons-material/TranslateRounded";
import {
  Box,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography
} from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { getLanguageMeta, supportedLanguages, type SupportedLanguage } from "@/i18n/config";

export function LanguageControl() {
  const { i18n, t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const currentLanguage = getLanguageMeta(i18n.language);
  const open = Boolean(anchorEl);

  const selectLanguage = (languageCode: SupportedLanguage) => {
    setAnchorEl(null);
    void i18n.changeLanguage(languageCode);
  };

  return (
    <Box component="span" data-geo-no-auto-translate translate="no" sx={{ display: "inline-flex" }}>
      <Tooltip title={t("language.current", { language: currentLanguage.label })}>
        <IconButton
          aria-label={t("language.change")}
          aria-haspopup="menu"
          aria-expanded={open ? "true" : undefined}
          onClick={(event) => setAnchorEl(event.currentTarget)}
          size="small"
          sx={{
            width: 44,
            height: 40,
            border: 0,
            borderRadius: 1,
            gap: 0.35,
            overflow: "hidden"
          }}
        >
          <TranslateRoundedIcon fontSize="small" />
          <Typography component="span" sx={{ fontSize: "0.65rem", fontWeight: 900 }}>
            {currentLanguage.shortLabel}
          </Typography>
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        slotProps={{
          list: {
            "aria-label": t("language.menuTitle"),
            className: "notranslate",
            translate: "no"
          },
          paper: {
            className: "notranslate",
            translate: "no"
          }
        }}
      >
        {supportedLanguages.map((language) => {
          const selected = language.code === currentLanguage.code;

          return (
            <MenuItem
              key={language.code}
              selected={selected}
              onClick={() => selectLanguage(language.code)}
            >
              <ListItemIcon sx={{ minWidth: 32 }}>
                {selected ? <CheckRoundedIcon fontSize="small" /> : <Box sx={{ width: 20 }} />}
              </ListItemIcon>
              <Stack spacing={0}>
                <Typography sx={{ fontWeight: 800 }}>{language.shortLabel}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {language.label}
                </Typography>
              </Stack>
            </MenuItem>
          );
        })}
      </Menu>
    </Box>
  );
}
